import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { RootState } from '../../store/store';
import { computeTourPlacement } from '@skygroup/shared/utils/tourPlacement';
import './site-engine-module-tour.less';

// ── In-iframe module-actions tour ───────────────────────────────────────────
// Runs INSIDE the site-engine preview iframe (this B2C app, loaded with
// ?editMode=true). The Backoffice site-engine tour can't highlight these
// buttons because they live in this separate document — so when BO opens the
// iframe with &seTour=1, this overlay takes over the module-actions
// walkthrough here and posts a "done" message back to BO when finished.
//
// Flow:
//   1. Intro card: "hover over any module to reveal its action toolbar".
//   2. On first hover of a module wrapper, lock onto it (force its action
//      buttons visible) and step through each available action button.
//   3. Finish → clean up + postMessage SiteEngineModuleTourDone to the parent.

const CARD_WIDTH = 320;
const CARD_HEIGHT_ESTIMATE = 190;
const SPOTLIGHT_PAD = 6;
const LOCK_CLASS = 'obs-se-module-locked';
// Added to <body> while a module is locked — suppresses hover-reveal on every
// other module so only the locked one's toolbar shows ("freeze time").
const BODY_ACTIVE_CLASS = 'obs-se-tour-active';

interface ActionStepDef {
  /** Resolver scoped to the locked module wrapper. */
  selector: string;
  titleKey: string;
  descKey: string;
  fallbackTitle: string;
  fallbackDesc: string;
}

// Ordered to match the visual layout: add-top, reorder arrows, edit, delete,
// add-bottom. Steps whose element isn't present on the hovered module
// (e.g. allow-only-edit modules) are filtered out at lock time.
const ACTION_STEPS: ActionStepDef[] = [
  {
    selector: '.add-btn-top',
    titleKey: 'seModAddTopTitle',
    descKey: 'seModAddTopDesc',
    fallbackTitle: 'Add Module Above',
    fallbackDesc:
      'Inserts a brand-new module directly above this one. Click it to pick a module type and drop it into place.',
  },
  {
    selector: '.arrows-wrapper',
    titleKey: 'seModReorderTitle',
    descKey: 'seModReorderDesc',
    fallbackTitle: 'Reorder This Module',
    fallbackDesc:
      'Use the up and down arrows to move this module earlier or later on the page. The preview reorders instantly.',
  },
  {
    selector: '.edit',
    titleKey: 'seModEditTitle',
    descKey: 'seModEditDesc',
    fallbackTitle: 'Edit This Module',
    fallbackDesc:
      'Opens this module in the settings panel on the left, where you can change its content, layout, and styling.',
  },
  {
    selector: '.delete',
    titleKey: 'seModDeleteTitle',
    descKey: 'seModDeleteDesc',
    fallbackTitle: 'Remove This Module',
    fallbackDesc:
      'Deletes this module from the page. You can always add it back later from the add-module options.',
  },
  {
    selector: '.add-btn-bottom',
    titleKey: 'seModAddBottomTitle',
    descKey: 'seModAddBottomDesc',
    fallbackTitle: 'Add Module Below',
    fallbackDesc:
      'Inserts a brand-new module directly below this one — the same as the top button, just for the slot underneath.',
  },
];

const SiteEngineModuleTour: React.FC = () => {
  const [searchParams] = useSearchParams();
  const translations = useSelector((s: RootState) => s.config.translations);
  const isRtl = useSelector((s: RootState) => s.config.direction === 'rtl');
  const to = (translations as any)?.onboarding ?? {};
  // Step content lives under onboarding.tour.*; the shared button labels
  // (Next / Back / Done) live at onboarding.* (top level).
  const tour = to.tour ?? {};

  const tx = (key: string, fallback: string) => tour[key] || fallback;

  // The tour is armed either by the &seTour=1 iframe URL flag OR by an
  // explicit postMessage from the Backoffice (more reliable — the URL flag
  // can be dropped by the iframe's own router on navigation). Both require
  // edit mode.
  const isEditMode = searchParams.get('editMode') === 'true';
  const [armedByMessage, setArmedByMessage] = useState(false);
  const active = isEditMode && (searchParams.get('seTour') === '1' || armedByMessage);

  // Listen for the Backoffice "arm" signal (sent on iframe load while the
  // site-engine tour is running).
  useEffect(() => {
    const onMsg = (e: MessageEvent) => {
      if (e?.data?.type === 'ArmSiteEngineModuleTour') setArmedByMessage(true);
      if (e?.data?.type === 'DisarmSiteEngineModuleTour') setArmedByMessage(false);
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, []);

  // Handshake: announce readiness to the parent on mount so the Backoffice
  // can arm us even if its iframe-onLoad arm fired before this component
  // mounted (race-proof — covers both message orderings).
  useEffect(() => {
    if (!isEditMode) return;
    try {
      window.parent?.postMessage({ type: 'SiteEngineModuleTourReady' }, '*');
    } catch {
      /* no parent — no-op */
    }
  }, [isEditMode]);

  // null = intro (waiting for hover); element = locked module wrapper.
  const [lockedWrapper, setLockedWrapper] = useState<HTMLElement | null>(null);
  const [steps, setSteps] = useState<ActionStepDef[]>([]);
  const [stepIndex, setStepIndex] = useState(-1); // -1 = intro
  const [cardPos, setCardPos] = useState<{ top: number; left: number } | null>(null);
  const [spotlight, setSpotlight] = useState<{ top: number; left: number; width: number; height: number } | null>(null);
  const [finished, setFinished] = useState(false);

  const cardRef = useRef<HTMLDivElement>(null);

  const cleanupLock = useCallback(() => {
    if (lockedWrapper) lockedWrapper.classList.remove(LOCK_CLASS);
    try {
      document.body.classList.remove(BODY_ACTIVE_CLASS);
    } catch {
      /* no-op */
    }
  }, [lockedWrapper]);

  // Lock onto a hovered module: freeze its toolbar to the hovered appearance
  // and suppress every other module's hover-reveal so the page holds still on
  // the module being explained. Builds the step list from whichever action
  // elements this particular module actually exposes (an allow-only-edit
  // module shows just the edit button; a module with no toolbar won't lock).
  const lockOnto = useCallback((wrapper: HTMLElement | null) => {
    if (!wrapper) return;
    if (!wrapper.querySelector('.EDIT-BUTTON')) return;
    const available = ACTION_STEPS.filter((s) => wrapper.querySelector(s.selector));
    if (!available.length) return;

    wrapper.classList.add(LOCK_CLASS);
    try {
      document.body.classList.add(BODY_ACTIVE_CLASS);
    } catch {
      /* no-op */
    }
    setLockedWrapper(wrapper);
    setSteps(available);
    setStepIndex(0);
  }, []);

  // ── Hover detection (until a module is locked) ───────────────────────────
  // Primary signal: the custom `obs-se-module-hover` event dispatched by
  // EditComponent the moment a real module wrapper is entered (and its
  // toolbar appears). Fallback: a capture-phase document mouseover that
  // resolves the nearest module wrapper — covers any module not wired to
  // dispatch the event.
  useEffect(() => {
    if (!active || finished) return;
    if (lockedWrapper) return; // already locked

    const onCustomHover = (e: Event) => {
      const el = (e as CustomEvent).detail?.el as HTMLElement | null;
      lockOnto(el ?? null);
    };
    const onOver = (e: Event) => {
      const target = e.target as Element | null;
      const wrapper = target?.closest?.('[id$="-wrapper"]:not([id$="-layout-wrapper"])') as HTMLElement | null;
      lockOnto(wrapper);
    };

    window.addEventListener('obs-se-module-hover', onCustomHover);
    document.addEventListener('mouseover', onOver, true);
    return () => {
      window.removeEventListener('obs-se-module-hover', onCustomHover);
      document.removeEventListener('mouseover', onOver, true);
    };
  }, [active, finished, lockedWrapper, lockOnto]);

  // ── Position the card + spotlight for the current step ──────────────────
  const reposition = useCallback(() => {
    if (!active) return;
    // Before a module is locked, nothing is shown — the "hover a module"
    // instruction comes from the Backoffice tour card, so this overlay
    // stays silent until the user actually hovers a module.
    if (stepIndex < 0 || !lockedWrapper) return;

    const step = steps[stepIndex];
    if (!step) return;
    const el = lockedWrapper.querySelector<HTMLElement>(step.selector);
    if (!el) return;

    const r = el.getBoundingClientRect();
    const sTop = r.top - SPOTLIGHT_PAD;
    const sLeft = r.left - SPOTLIGHT_PAD;
    const sWidth = r.width + SPOTLIGHT_PAD * 2;
    const sHeight = r.height + SPOTLIGHT_PAD * 2;
    setSpotlight({ top: sTop, left: sLeft, width: sWidth, height: sHeight });

    const cardEl = cardRef.current;
    const placement = computeTourPlacement({
      anchor: { top: sTop, left: sLeft, width: sWidth, height: sHeight },
      viewport: { width: window.innerWidth, height: window.innerHeight },
      card: {
        width: cardEl?.offsetWidth || CARD_WIDTH,
        height: cardEl?.offsetHeight || CARD_HEIGHT_ESTIMATE,
      },
      isRtl,
    });
    setCardPos({ top: placement.top, left: placement.left });
  }, [active, stepIndex, steps, lockedWrapper, isRtl]);

  // Bring the current target into view, then position.
  useEffect(() => {
    if (!active || finished) return;
    if (stepIndex >= 0 && lockedWrapper) {
      const step = steps[stepIndex];
      const el = step && lockedWrapper.querySelector<HTMLElement>(step.selector);
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    const id = window.setTimeout(reposition, 320);
    return () => window.clearTimeout(id);
  }, [active, finished, stepIndex, lockedWrapper, reposition]);

  // Keep glued on scroll / resize.
  useEffect(() => {
    if (!active || finished) return;
    const handler = () => reposition();
    window.addEventListener('scroll', handler, { capture: true, passive: true });
    window.addEventListener('resize', handler);
    return () => {
      window.removeEventListener('scroll', handler, { capture: true } as any);
      window.removeEventListener('resize', handler);
    };
  }, [active, finished, reposition]);

  // Clean up the forced-visible class on unmount.
  useEffect(() => cleanupLock, [cleanupLock]);

  // Render nothing until the user hovers a module and we lock onto it.
  // The hover listener (effect above) stays active in the meantime.
  if (!active || finished || stepIndex < 0 || !lockedWrapper) return null;

  const isLast = stepIndex === steps.length - 1;
  const step = steps[stepIndex];
  if (!step) return null;

  const handleFinish = () => {
    cleanupLock();
    setFinished(true);
    try {
      window.parent?.postMessage({ type: 'SiteEngineModuleTourDone' }, '*');
    } catch {
      /* parent unavailable — no-op */
    }
  };

  const handleClose = () => {
    cleanupLock();
    setFinished(true);
    try {
      window.parent?.postMessage({ type: 'SiteEngineModuleTourClosed' }, '*');
    } catch {
      /* no-op */
    }
  };

  const handleNext = () => {
    if (isLast) handleFinish();
    else setStepIndex((i) => i + 1);
  };

  const handlePrev = () => {
    if (stepIndex > 0) setStepIndex((i) => i - 1);
  };

  return (
    <div className="se-mod-tour">
      {/* Transparent full-screen click blocker. While we're describing the
          toolbar buttons (edit / delete are destructive) the page must not
          respond to clicks — the user navigates with the card's buttons.
          Hover still works through it for the spotlight, but clicks are
          swallowed. */}
      <div className="se-mod-tour__blocker" />

      {/* Dimmed backdrop with a cut-out around the current action button. */}
      {spotlight && (
        <>
          <div className="se-mod-tour__backdrop" style={{ top: 0, left: 0, right: 0, height: Math.max(0, spotlight.top) }} />
          <div className="se-mod-tour__backdrop" style={{ top: spotlight.top + spotlight.height, left: 0, right: 0, bottom: 0 }} />
          <div className="se-mod-tour__backdrop" style={{ top: spotlight.top, left: 0, width: Math.max(0, spotlight.left), height: spotlight.height }} />
          <div className="se-mod-tour__backdrop" style={{ top: spotlight.top, left: spotlight.left + spotlight.width, right: 0, height: spotlight.height }} />
          <div
            className="se-mod-tour__frame"
            style={{ top: spotlight.top, left: spotlight.left, width: spotlight.width, height: spotlight.height }}
          />
        </>
      )}

      <div
        ref={cardRef}
        className="se-mod-tour__card"
        style={{
          top: cardPos?.top ?? 24,
          left: cardPos?.left ?? 24,
          width: CARD_WIDTH,
          visibility: cardPos ? 'visible' : 'hidden',
        }}
      >
        <button type="button" className="se-mod-tour__close" onClick={handleClose} aria-label="Close">
          ×
        </button>

        <div className="se-mod-tour__dots">
          {steps.map((_, i) => (
            <span
              key={i}
              className={`se-mod-tour__dot${i === stepIndex ? ' se-mod-tour__dot--active' : i < stepIndex ? ' se-mod-tour__dot--done' : ''}`}
            />
          ))}
        </div>
        <div className="se-mod-tour__title">{tx(step.titleKey, step.fallbackTitle)}</div>
        <div className="se-mod-tour__desc">{tx(step.descKey, step.fallbackDesc)}</div>
        <div className="se-mod-tour__footer">
          {stepIndex > 0 && (
            <button type="button" className="se-mod-tour__btn se-mod-tour__btn--ghost" onClick={handlePrev}>
              {to.tourPrevBtn || 'Back'}
            </button>
          )}
          <div className="se-mod-tour__spacer" />
          <button type="button" className="se-mod-tour__btn se-mod-tour__btn--primary" onClick={handleNext}>
            {isLast ? (to.tourFinishBtn || 'Done') : (to.tourNextBtn || 'Next')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SiteEngineModuleTour;
